import * as Sentry from "@sentry/react";

import { getRestApiUrl } from "./backendOverride";
import {
  TELEMETRY_SOURCES,
  createTelemetryReporter,
  knownTelemetrySource,
} from "./conversationTelemetry";

/**
 * Transport and Redux wiring for conversation isolation telemetry.
 *
 * On `sendBeacon`: it cannot carry an `Authorization` header, and the receiving
 * endpoint is authenticated, so this uses `fetch` with `keepalive: true`
 * instead. That keeps the property that matters — the request survives the page
 * being navigated away from, and never blocks the render path — while still
 * sending the bearer token. Putting the token in the URL to satisfy sendBeacon
 * would have leaked it into every access log between here and the server.
 *
 * The middleware exists so the check lives in ONE place rather than at each of
 * the seven points where a conversation can enter the store. Two of those seven
 * — the deep-link path and the markAsUnread insert — have no isolation check on
 * them at all today, which is exactly the gap a per-call-site approach
 * reproduces.
 */

const ENDPOINT = "/telemetry/conversation-anomaly";

/**
 * HTTP statuses that mean "this token will not be accepted here".
 *
 * `fetch` FULFILLS on 401/403 — the rejection handler below only runs for a
 * network-level failure — so an endpoint that rejects the token would otherwise
 * be retried on every flush for as long as the tab stays open, silently.
 */
const AUTH_FAILURE_STATUSES = [401, 403];

/**
 * The token the endpoint last refused.
 *
 * Deliberately NOT a plain boolean: a refusal is a property of the token, not
 * of the session, so a later login with a different token starts sending again
 * without a reload.
 *
 * Just as deliberately, an auth failure here does NOT clear `localStorage` and
 * does NOT log anybody out. That belongs to the axios response interceptor,
 * which owns the real API traffic. This module is a diagnostic observer: if the
 * telemetry route is missing, misconfigured, or briefly fronted by a gateway
 * that answers 403, logging the user out of the dashboard over it would be the
 * telemetry breaking the product it is supposed to be watching.
 */
let refusedToken = null;

/**
 * Statuses that mean "this route does not exist on the server answering".
 *
 * That is the dashboard deployed BEFORE backend#366 (Vercel ships on merge, the
 * backend waits for a window). It is not a property of the token, so it is not
 * remembered per token: it is a pause, after which sending resumes on its own —
 * the backend getting deployed must not need every open tab to reload. The
 * length is a retry backoff and nothing else: findings flushed inside it are
 * dropped, exactly as they were being dropped by the 404 itself.
 */
const ROUTE_MISSING_STATUSES = [404, 405];
export const ROUTE_MISSING_PAUSE_MS = 10 * 60 * 1000;
let pausedUntil = 0;

/**
 * Unexpected failures go to Sentry ONCE per operation per page load. The
 * middleware runs on every Redux action, so an uncapped capture would turn one
 * bug into thousands of events. Only the error and two fixed tags travel: no
 * state, no payload, no token. An HTTP status or an offline browser is not an
 * unexpected failure and is not reported.
 */
let reportedFaults = {};

function reportTelemetryFault(op, error) {
  try {
    if (reportedFaults[op]) return;
    reportedFaults[op] = true;
    Sentry.captureException(error, {
      tags: { component: "conversationTelemetry", op },
    });
  } catch (e) {
    // Reporting the failure must not become the failure.
  }
}

/** Test seam. Module state would otherwise leak between test cases. */
export function resetTelemetryTransport() {
  refusedToken = null;
  pausedUntil = 0;
  reportedFaults = {};
}

/**
 * The default label for each action type in `conversationsDuck` that writes
 * `state.data`, used ONLY when the dispatch site did not state one with
 * `withTelemetrySource`.
 *
 * `SET_CONVERSATIONS` defaults to `redux`, never to `pusher`: it is dispatched
 * from ~20 places (pagination, markAsRead, delete, delivery receipts, tags…)
 * and the type alone cannot tell which. The sites that really are a Pusher
 * event inserting a conversation, and the pagination merge, label themselves.
 *
 * The types are string literals there, not imported, to keep this module out
 * of the duck's import graph.
 */
export const TELEMETRY_ACTION_SOURCES = {
  GET_CONVERSATIONS_SUCCESS: TELEMETRY_SOURCES.QUERY,
  SET_CONVERSATIONS: TELEMETRY_SOURCES.REDUX,
  SET_CONVERSATION_AS_SELECTED: TELEMETRY_SOURCES.SELECTION,
  UPDATE_VALUABLE_CLASSIFICATION: TELEMETRY_SOURCES.UPDATE,
  UPDATE_HASFOLLOWUPMESSAGE: TELEMETRY_SOURCES.UPDATE,
};

/** The label for an action, or null when the action does not write the list. */
function sourceOf(action) {
  const type = action && action.type;
  if (!Object.prototype.hasOwnProperty.call(TELEMETRY_ACTION_SOURCES, type)) {
    return null;
  }
  const meta = action.meta;
  const stated =
    meta && typeof meta === "object"
      ? knownTelemetrySource(meta.telemetrySource)
      : null;
  return stated || TELEMETRY_ACTION_SOURCES[type];
}

/**
 * The open conversation when it is NOT also in the list, otherwise null.
 *
 * A deep link (`getConversationsAction` with a `chatId`) selects a conversation
 * the list query never returned, so it lives only in `current` and inspecting
 * the list alone never saw it. When it IS in the list it was already inspected
 * under the list's own label, and inspecting it again would only inflate
 * `inspected`.
 */
function selectedOutsideList(current, conversations) {
  if (!current || typeof current !== "object") return null;
  const id = current.id;
  if (typeof id !== "string" || id.trim().length === 0) return null;
  if (Array.isArray(conversations)) {
    for (let i = 0; i < conversations.length; i += 1) {
      const row = conversations[i];
      if (row && typeof row === "object" && row.id === id) return null;
    }
  }
  return current;
}

/** Fire and forget. Never throws, never rejects, never surfaces to the user. */
export function sendTelemetry(payload) {
  try {
    if (typeof fetch !== "function") return;

    const token =
      typeof localStorage !== "undefined" && localStorage
        ? localStorage.getItem("token")
        : null;
    if (!token) return;
    if (refusedToken !== null && token === refusedToken) return;
    if (Date.now() < pausedUntil) return;

    const result = fetch(`${getRestApiUrl()}${ENDPOINT}`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (result && typeof result.then === "function") {
      result.then(
        (response) => {
          try {
            const status = response ? response.status : undefined;
            if (AUTH_FAILURE_STATUSES.indexOf(status) !== -1) {
              refusedToken = token;
            } else if (ROUTE_MISSING_STATUSES.indexOf(status) !== -1) {
              pausedUntil = Date.now() + ROUTE_MISSING_PAUSE_MS;
            }
          } catch (e) {
            // Inspecting the response must not reject the promise.
            reportTelemetryFault("inspectResponse", e);
          }
          return undefined;
        },
        () => undefined
      );
    }
  } catch (e) {
    // Telemetry must never break the page it observes.
    reportTelemetryFault("sendTelemetry", e);
  }
}

/**
 * Redux middleware. Inspects the store AFTER the reducer has run: reading
 * before would inspect the previous list and miss the very conversation the
 * action just introduced.
 *
 * The identity is observed on EVERY action, not only on the five that write
 * conversations. Logout is client-side routing, so the reporter survives it;
 * watching only the conversation actions would leave a window between the new
 * agent logging in and their first conversation arriving, and a flush landing
 * inside that window would post the previous agent's findings under the new
 * agent's token. The observation is a property read and a string comparison.
 */
export function createConversationTelemetryMiddleware(reporter) {
  return (store) => {
    // The last `current` inspected, by reference. Reducers replace the object
    // whenever they change it, so an unchanged reference means nothing new to
    // look at — whichever action ran.
    let lastCurrent = null;

    return (next) => (action) => {
      const result = next(action);
      try {
        const state = store.getState();
        const user = state && state.user ? state.user.data : null;
        if (typeof reporter.observeIdentity === "function") {
          reporter.observeIdentity(user);
        }

        const list = state && state.conversations ? state.conversations : null;
        // `state.spaces.selected` is the space the user is looking at. It is
        // legitimately `{}` until the bootstrap query resolves, and the
        // reporter treats that as "do not judge" rather than as "everything is
        // wrong". `GET_CONVERSATIONS` sets `fetching` without clearing `data`,
        // so while it is true the list may still be the previous space's inbox.
        const activeSpace = state && state.spaces ? state.spaces.selected : null;
        const listState = { fetching: !!list && list.fetching === true };

        const src = sourceOf(action);
        if (src) reporter.record(user, list ? list.data : null, src, activeSpace, listState);

        const current = list ? list.current : null;
        if (current !== lastCurrent) {
          lastCurrent = current;
          const selected = selectedOutsideList(current, list ? list.data : null);
          if (selected) {
            reporter.record(user, [selected], TELEMETRY_SOURCES.SELECTED, activeSpace, listState);
          }
        }
      } catch (e) {
        // Never break the store over telemetry.
        reportTelemetryFault("middleware", e);
      }
      return result;
    };
  };
}

/** The reporter the app uses, wired to the real transport. */
export const conversationTelemetryReporter = createTelemetryReporter({
  send: sendTelemetry,
});

export default createConversationTelemetryMiddleware;
